var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dismissLateReply, endSupportSession, getSupportAttachmentUrl, getSupportState, markSupportRead, newClientRequestId, parseSupportError, rejoinSupportSession, sendSupportMessage, startSupportHandoff } from '../services/chatApi';
import { refreshFileDownloadUrl } from '../services/uploadApi';
import { AttachmentScanError } from '../helpers/attachmentScan';
import { supportKeys } from './queryKeys';
import { useSupportEventStream } from './useSupportEventStream';
import { HTTP_STATUS_CONFLICT, HTTP_STATUS_NOT_FOUND, HTTP_STATUS_UNPROCESSABLE, MESSAGEABLE_SUPPORT_MODES, SUPPORT_CHANNEL_EMAIL, SUPPORT_COPY, SUPPORT_DELIVERY, SUPPORT_ATTACHMENT_DISPOSITION, SUPPORT_ATTACHMENT_URL_SKEW_MS, SUPPORT_ERROR, SUPPORT_EVENT_DEDUPE_LIMIT, SUPPORT_MAX_FILES_PER_MESSAGE, SUPPORT_MODE, SUPPORT_MESSAGE_ORIGIN, SUPPORT_ROLE_HUMAN, SUPPORT_SSE_EVENT, SUPPORT_UPLOAD_DOWNLOAD_SOURCE } from '../constants/support';
import { isActiveSupportMode, shouldAdoptSession, supportNoticeToChatMessage, supportPayloadToChatMessage } from '../helpers/supportHelpers';
const PENDING_ID_PREFIX = 'support-pending-';
const SNAPSHOT_STALE_MS = 30000;
/**
 * The attachment route refuses a file whose scan is not clean with a 409 (`SCAN_PENDING`: not yet;
 * `SCAN_FAILED`: never). Those are not failures of the request but a verdict on the file, so they
 * become an `AttachmentScanError` the chip renders the same way as a scan status on the message.
 */
function scanErrorFrom(error) {
    const parsed = parseSupportError(error);
    if ((parsed === null || parsed === void 0 ? void 0 : parsed.status) !== HTTP_STATUS_CONFLICT)
        return null;
    if (parsed.body.error === SUPPORT_ERROR.SCAN_PENDING)
        return new AttachmentScanError('scanning');
    if (parsed.body.error === SUPPORT_ERROR.SCAN_FAILED)
        return new AttachmentScanError('unchecked');
    return null;
}
/**
 * One authorized URL for a file in the support conversation. The support route is asked first; a
 * 404 means it does not know the file as part of a persisted support message (a customer's own
 * upload still in flight, say), so the widget's existing path for its own uploads
 * (`/api/files/{ref}/refresh-url`) is used. Both are tenant-checked server-side, and neither
 * ever returns a provider URL.
 */
function fetchAttachmentUrl(conversationId, fileId, disposition, source) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        if (source === 'upload') {
            // The customer's own file on a message that is not persisted yet: the support route cannot
            // know it, so do not ask it (that is a guaranteed 404). Go straight to the upload path.
            const uploaded = yield refreshFileDownloadUrl(fileId, SUPPORT_UPLOAD_DOWNLOAD_SOURCE);
            return { url: uploaded.download_url, expires_at: (_a = uploaded.expires_at) !== null && _a !== void 0 ? _a : null };
        }
        try {
            const fetched = yield getSupportAttachmentUrl(conversationId, fileId, disposition);
            return { url: fetched.url, expires_at: fetched.expires_at };
        }
        catch (error) {
            const scanError = scanErrorFrom(error);
            if (scanError)
                throw scanError;
            if (((_b = parseSupportError(error)) === null || _b === void 0 ? void 0 : _b.status) !== HTTP_STATUS_NOT_FOUND)
                throw error;
            const refreshed = yield refreshFileDownloadUrl(fileId, SUPPORT_UPLOAD_DOWNLOAD_SOURCE);
            return { url: refreshed.download_url, expires_at: (_c = refreshed.expires_at) !== null && _c !== void 0 ? _c : null };
        }
    });
}
function useDocumentVisible() {
    const [visible, setVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden');
    useEffect(() => {
        const onChange = () => setVisible(document.visibilityState !== 'hidden');
        document.addEventListener('visibilitychange', onChange);
        return () => document.removeEventListener('visibilitychange', onChange);
    }, []);
    return visible;
}
function withDelivery(message, delivery) {
    return Object.assign(Object.assign({}, message), { metadata: Object.assign(Object.assign({}, message.metadata), { delivery }) });
}
export function useSupportSession({ conversationId, isViewing, messageCache, onDraftRejected }) {
    var _a, _b;
    // The session AND the conversation it belongs to. A switch of conversation only resets state in an
    // effect, i.e. AFTER the first render for the new conversation: without the owner check that
    // render would report the PREVIOUS conversation's session (live chat in A showing as "active" in
    // B). `session` below is the session of the VIEWED conversation, or null.
    const [held, setHeld] = useState(null);
    const session = held !== null && held.conversationId === conversationId ? held.session : null;
    const [snapshotUnread, setSnapshotUnread] = useState(0);
    const [unreadSeqs, setUnreadSeqs] = useState(() => new Map());
    const [notice, setNotice] = useState(null);
    const [isStarting, setIsStarting] = useState(false);
    const [isEnding, setIsEnding] = useState(false);
    // Late-reply prompt: dismissed locally while the server catches up; closed once the support
    // side refuses a rejoin; working while a continue call is in flight.
    const [lateDismissed, setLateDismissed] = useState(false);
    const [lateClosed, setLateClosed] = useState(false);
    const [lateWorking, setLateWorking] = useState(false);
    const [isContinuing, setIsContinuing] = useState(false);
    const [endedSessionId, setEndedSessionId] = useState(null);
    const [dismissedEndedId, setDismissedEndedId] = useState(null);
    const isVisible = useDocumentVisible();
    // Everything the stable callbacks below read lives in refs so their identity never churns
    // (useChatMessages' cache callbacks are re-created on every render).
    const sessionRef = useRef(null);
    const conversationIdRef = useRef(conversationId);
    const cacheRef = useRef(messageCache);
    cacheRef.current = messageCache;
    const onDraftRejectedRef = useRef(onDraftRejected);
    onDraftRejectedRef.current = onDraftRejected;
    const lastSequenceRef = useRef(0);
    const readThroughRef = useRef(0);
    const seenEventIdsRef = useRef([]);
    const pendingRef = useRef(new Map());
    const rejoinRequestIdRef = useRef(null);
    const attachmentUrlsRef = useRef(new Map());
    const attachmentRequestsRef = useRef(new Map());
    const snapshot = useQuery({
        queryKey: supportKeys.state(conversationId),
        queryFn: () => getSupportState(conversationId),
        enabled: Boolean(conversationId),
        staleTime: SNAPSHOT_STALE_MS,
        retry: 1,
        refetchOnWindowFocus: false
    });
    // The picker cap: what the server advertises, never anything but a positive whole number.
    const advertisedMaxFiles = (_a = snapshot.data) === null || _a === void 0 ? void 0 : _a.max_files;
    const maxFiles = typeof advertisedMaxFiles === 'number' && Number.isInteger(advertisedMaxFiles) && advertisedMaxFiles > 0
        ? advertisedMaxFiles
        : SUPPORT_MAX_FILES_PER_MESSAGE;
    const maxFilesRef = useRef(maxFiles);
    maxFilesRef.current = maxFiles;
    // ---- session adoption -------------------------------------------------------------
    const adopt = useCallback((incoming) => {
        const { current } = sessionRef;
        if (!shouldAdoptSession(current, incoming))
            return;
        if (current &&
            current.id === incoming.id &&
            isActiveSupportMode(current.mode) &&
            !isActiveSupportMode(incoming.mode)) {
            // Observed ending in THIS view; an old ended session loaded from history does not nag.
            setEndedSessionId(incoming.id);
        }
        if (incoming.last_read_sequence != null) {
            lastSequenceRef.current = Math.max(lastSequenceRef.current, incoming.last_read_sequence);
            readThroughRef.current = Math.max(readThroughRef.current, incoming.last_read_sequence);
        }
        if (incoming.late_reply_pending && !(current === null || current === void 0 ? void 0 : current.late_reply_pending)) {
            // A NEW late reply: whatever the customer did about the previous one no longer applies.
            setLateDismissed(false);
            setLateClosed(false);
        }
        sessionRef.current = incoming;
        setHeld({ session: incoming, conversationId: conversationIdRef.current });
    }, []);
    // A different conversation starts from nothing: no carried-over session, cursor or drafts.
    useEffect(() => {
        conversationIdRef.current = conversationId;
        sessionRef.current = null;
        lastSequenceRef.current = 0;
        readThroughRef.current = 0;
        seenEventIdsRef.current = [];
        pendingRef.current = new Map();
        rejoinRequestIdRef.current = null;
        attachmentUrlsRef.current = new Map();
        attachmentRequestsRef.current = new Map();
        setLateDismissed(false);
        setLateClosed(false);
        setLateWorking(false);
        setIsContinuing(false);
        setHeld(null);
        setSnapshotUnread(0);
        setUnreadSeqs(new Map());
        setNotice(null);
        setEndedSessionId(null);
        setDismissedEndedId(null);
    }, [conversationId]);
    useEffect(() => {
        const { data } = snapshot;
        if (!data)
            return;
        if (data.session)
            adopt(data.session);
        setSnapshotUnread(data.unread_count);
    }, [snapshot.data, adopt]);
    const { refetch } = snapshot;
    const refresh = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (!conversationIdRef.current)
            return;
        const result = yield refetch();
        if ((_a = result.data) === null || _a === void 0 ? void 0 : _a.session)
            adopt(result.data.session);
    }), [refetch, adopt]);
    // ---- live events --------------------------------------------------------------------
    const reconcileSend = useCallback((pending, realId, delivery, sequence) => {
        const cache = cacheRef.current;
        if (pending.messageId !== realId) {
            // The server's own echo can land before the 202 does; keep exactly one bubble either way.
            if (cache.hasMessage(realId))
                cache.removeMessage(pending.messageId);
            else
                cache.updateMessage(pending.messageId, (m) => (Object.assign(Object.assign({}, m), { id: realId })));
            pending.messageId = realId;
        }
        // The sequence is what orders this bubble against the agent's replies; take it as soon as
        // the server has assigned one (the 202 or the echo, whichever lands first).
        cache.updateMessage(realId, (m) => {
            const next = withDelivery(m, delivery);
            return sequence != null && next.sequence == null ? Object.assign(Object.assign({}, next), { sequence }) : next;
        });
    }, []);
    const handleMessageEvent = useCallback((envelope) => {
        const { payload, sequence } = envelope;
        if (sequence != null)
            lastSequenceRef.current = Math.max(lastSequenceRef.current, sequence);
        if (payload.role === SUPPORT_ROLE_HUMAN) {
            cacheRef.current.appendMessage(supportPayloadToChatMessage(envelope));
            if (sequence != null && sequence > readThroughRef.current) {
                setUnreadSeqs((prev) => new Map(prev).set(payload.message_id, sequence));
            }
            return;
        }
        // The customer's own message echoed back. Adopt it into the matching optimistic bubble
        // (same text, still pending) instead of appending a second copy; otherwise it came from
        // another tab and is appended (deduped by id).
        const match = [...pendingRef.current.values()].find((pending) => pending.text === payload.text.trim() && pending.messageId.startsWith(PENDING_ID_PREFIX));
        if (match) {
            reconcileSend(match, payload.message_id, SUPPORT_DELIVERY.SENT, sequence);
            return;
        }
        cacheRef.current.appendMessage(supportPayloadToChatMessage(envelope));
    }, [reconcileSend]);
    // A system notice ("You started a chat..."): appended as the history row it mirrors. It is NOT a
    // support message: it never counts as unread and never moves the catch-up cursor (the stream
    // does not replay it, and a notice's sequence must not skip a real message below it).
    const handleNoticeEvent = useCallback((envelope) => {
        const noticeMessage = supportNoticeToChatMessage(envelope);
        if (noticeMessage)
            cacheRef.current.appendMessage(noticeMessage); // deduped by id against what history already holds
    }, []);
    const handleEvent = useCallback((name, streamEnvelope) => {
        var _a, _b, _c;
        const seen = seenEventIdsRef.current;
        if (seen.includes(streamEnvelope.event_id))
            return;
        seen.push(streamEnvelope.event_id);
        if (seen.length > SUPPORT_EVENT_DEDUPE_LIMIT)
            seen.shift();
        if (name === SUPPORT_SSE_EVENT.NOTICE) {
            handleNoticeEvent(streamEnvelope);
            return;
        }
        // Every other event carries a session: its id and mode_version are never null.
        const envelope = streamEnvelope;
        if (name === SUPPORT_SSE_EVENT.STATE) {
            const payload = envelope.payload;
            const { current } = sessionRef;
            if (current && current.id !== envelope.support_session_id && isActiveSupportMode(current.mode)) {
                void refresh(); // a newer session replaced the one we hold; take the server's word
                return;
            }
            adopt(Object.assign(Object.assign({}, current), { id: envelope.support_session_id, mode: payload.mode, mode_version: envelope.mode_version, channel: payload.channel, end_reason: (_a = payload.end_reason) !== null && _a !== void 0 ? _a : null, last_human_message_at: (_b = payload.last_human_message_at) !== null && _b !== void 0 ? _b : null, late_reply_pending: (_c = payload.late_reply_pending) !== null && _c !== void 0 ? _c : false }));
            return;
        }
        if (name === SUPPORT_SSE_EVENT.MESSAGE) {
            handleMessageEvent(envelope);
            return;
        }
        if (name === SUPPORT_SSE_EVENT.DELIVERY) {
            const payload = envelope.payload;
            const delivery = payload.delivery === 'failed' ? SUPPORT_DELIVERY.FAILED : SUPPORT_DELIVERY.SENT;
            cacheRef.current.updateMessage(payload.message_id, (m) => withDelivery(m, delivery));
        }
    }, [adopt, handleMessageEvent, handleNoticeEvent, refresh]);
    const getAfterSequence = useCallback(() => lastSequenceRef.current, []);
    // Held open while a session exists (ended sessions too: a late agent reply still arrives).
    const { connectionState } = useSupportEventStream({
        conversationId,
        enabled: Boolean(conversationId) && session !== null,
        getAfterSequence,
        onEvent: handleEvent
    });
    // ---- unread / mark read -------------------------------------------------------------
    const markRead = useCallback((through) => __awaiter(this, void 0, void 0, function* () {
        const { current } = sessionRef;
        const convId = conversationIdRef.current;
        if (!current || !convId || through <= readThroughRef.current)
            return;
        const previous = readThroughRef.current;
        readThroughRef.current = through;
        try {
            const updated = yield markSupportRead(convId, { support_session_id: current.id, through_sequence: through });
            if (conversationIdRef.current !== convId)
                return;
            // Cleared only once the server has it: a failed request must leave the badge showing what
            // the server still counts as unread.
            setSnapshotUnread(0);
            setUnreadSeqs((prev) => new Map([...prev].filter(([, seq]) => seq > through)));
            adopt(updated);
        }
        catch (error) {
            // Not read as far as the server is concerned; the next unread reply or reopen retries.
            readThroughRef.current = previous;
            console.error('[useSupportSession] Failed to mark support messages read:', error);
        }
    }), [adopt]);
    useEffect(() => {
        if (!isViewing || !isVisible || unreadSeqs.size === 0)
            return;
        void markRead(Math.max(...unreadSeqs.values()));
    }, [isViewing, isVisible, unreadSeqs, markRead]);
    // The snapshot count and the catch-up replay describe the SAME unread messages, so take the
    // larger rather than the sum.
    const unreadCount = Math.max(snapshotUnread, unreadSeqs.size);
    // ---- actions ------------------------------------------------------------------------
    const startHandoff = useCallback((_a) => __awaiter(this, [_a], void 0, function* ({ channel, offerId, clientRequestId, contactEmail }) {
        const convId = conversationIdRef.current;
        if (!convId)
            return { ok: false };
        setIsStarting(true);
        try {
            const result = yield startSupportHandoff(convId, Object.assign({ client_request_id: clientRequestId, channel, offer_id: offerId }, (channel === SUPPORT_CHANNEL_EMAIL && contactEmail ? { contact_email: contactEmail } : {})));
            if (result.channel === 'email') {
                return { ok: true, channel: 'email', contactEmail: result.contact_email };
            }
            adopt(result.support_session);
            return { ok: true, channel: 'live_chat' };
        }
        catch (error) {
            const parsed = parseSupportError(error);
            if (channel === 'live_chat' && (parsed === null || parsed === void 0 ? void 0 : parsed.status) === HTTP_STATUS_CONFLICT && parsed.body.support_session) {
                // A double click or another tab got there first: the chat IS open, show it.
                adopt(parsed.body.support_session);
                return { ok: true, channel: 'live_chat' };
            }
            return { ok: false, errorCode: parsed === null || parsed === void 0 ? void 0 : parsed.body.error };
        }
        finally {
            setIsStarting(false);
        }
    }), [adopt]);
    const deliver = useCallback((pending) => __awaiter(this, void 0, void 0, function* () {
        const convId = conversationIdRef.current;
        const { current } = sessionRef;
        const cache = cacheRef.current;
        if (!convId || !current)
            return;
        setNotice(null);
        cache.updateMessage(pending.messageId, (m) => withDelivery(m, SUPPORT_DELIVERY.SENDING));
        try {
            const result = yield sendSupportMessage(convId, {
                support_session_id: current.id,
                mode_version: current.mode_version,
                client_request_id: pending.clientRequestId,
                text: pending.text,
                file_ids: pending.fileIds
            });
            adopt(result.support_session);
            if (result.sequence != null) {
                lastSequenceRef.current = Math.max(lastSequenceRef.current, result.sequence);
                reconcileSend(pending, result.message_id, SUPPORT_DELIVERY.SENT, result.sequence);
                pendingRef.current.delete(pending.clientRequestId);
            }
            else {
                // Another request for this same send is mid-flight; its delivery event settles it.
                reconcileSend(pending, result.message_id, SUPPORT_DELIVERY.SENDING);
            }
        }
        catch (error) {
            const parsed = parseSupportError(error);
            if ((parsed === null || parsed === void 0 ? void 0 : parsed.status) === HTTP_STATUS_CONFLICT) {
                // The chat changed state under the send. Nothing was forwarded: show the CURRENT
                // state, take the bubble back, and return the draft for an explicit resend.
                if (parsed.body.support_session)
                    adopt(parsed.body.support_session);
                else
                    void refresh();
                cache.removeMessage(pending.messageId);
                pendingRef.current.delete(pending.clientRequestId);
                onDraftRejectedRef.current(pending.text, pending.draftFiles);
                setNotice(SUPPORT_COPY.CONFLICT_NOTICE);
                return;
            }
            if ((parsed === null || parsed === void 0 ? void 0 : parsed.status) === HTTP_STATUS_UNPROCESSABLE && parsed.body.error === SUPPORT_ERROR.TOO_MANY_FILES) {
                // More files than the server allows (our cap was stale or the picker was bypassed).
                // Nothing was forwarded and a resend would fail the same way: take the bubble back and
                // return the text AND the files (B40), exactly as for a 409, and say why.
                cache.removeMessage(pending.messageId);
                pendingRef.current.delete(pending.clientRequestId);
                onDraftRejectedRef.current(pending.text, pending.draftFiles);
                setNotice(SUPPORT_COPY.TOO_MANY_FILES_DRAFT_KEPT(maxFilesRef.current));
                return;
            }
            cache.updateMessage(pending.messageId, (m) => withDelivery(m, SUPPORT_DELIVERY.FAILED));
        }
    }), [adopt, reconcileSend, refresh]);
    const sendMessage = useCallback((text_1, ...args_1) => __awaiter(this, [text_1, ...args_1], void 0, function* (text, options = {}) {
        var _a;
        const { current } = sessionRef;
        const trimmed = text.trim();
        const fileIds = (_a = options.fileIds) !== null && _a !== void 0 ? _a : [];
        if (!current || !MESSAGEABLE_SUPPORT_MODES.includes(current.mode) || (!trimmed && fileIds.length === 0))
            return;
        const clientRequestId = newClientRequestId();
        const pending = {
            clientRequestId,
            text: trimmed,
            fileIds,
            draftFiles: options.draftFiles,
            messageId: `${PENDING_ID_PREFIX}${clientRequestId}`
        };
        pendingRef.current.set(clientRequestId, pending);
        cacheRef.current.appendMessage({
            id: pending.messageId,
            role: 'user',
            content: trimmed,
            file_attachments: options.attachments && options.attachments.length > 0 ? options.attachments : undefined,
            metadata: {
                origin: SUPPORT_MESSAGE_ORIGIN,
                delivery: SUPPORT_DELIVERY.SENDING,
                client_request_id: clientRequestId
            },
            created_at: new Date().toISOString()
        });
        yield deliver(pending);
    }), [deliver]);
    const retrySend = useCallback((messageId) => __awaiter(this, void 0, void 0, function* () {
        const pending = [...pendingRef.current.values()].find((p) => p.messageId === messageId);
        if (pending)
            yield deliver(pending); // same client_request_id: the server dedupes a half-failed send
    }), [deliver]);
    const endChat = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const convId = conversationIdRef.current;
        const { current } = sessionRef;
        if (!convId || !current)
            return;
        setIsEnding(true);
        setNotice(null);
        try {
            adopt(yield endSupportSession(convId, { support_session_id: current.id, mode_version: current.mode_version }));
        }
        catch (_a) {
            setNotice(SUPPORT_COPY.END_FAILED);
        }
        finally {
            setIsEnding(false);
        }
    }), [adopt]);
    const noteAiSendRefused = useCallback(() => {
        setNotice(SUPPORT_COPY.CONFLICT_NOTICE);
        void refresh();
    }, [refresh]);
    // ---- late reply (the agent answered after the customer ended the chat) -----------------
    /**
     * The rejoin call behind every "continue this chat" action. `unavailable`: the server refused it
     * outright (nothing to continue). `moved`: the state changed under the click (another tab, a newer
     * version) and the current state was adopted instead. `failed`: a notice says so.
     */
    const rejoinCurrent = useCallback((convId, current) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        // One id per prompt: a retry after a dropped response is the SAME rejoin, not a second one.
        rejoinRequestIdRef.current = (_a = rejoinRequestIdRef.current) !== null && _a !== void 0 ? _a : newClientRequestId();
        try {
            adopt(yield rejoinSupportSession(convId, {
                support_session_id: current.id,
                mode_version: current.mode_version,
                client_request_id: rejoinRequestIdRef.current
            }));
            rejoinRequestIdRef.current = null;
            return 'continued';
        }
        catch (error) {
            const parsed = parseSupportError(error);
            if ((parsed === null || parsed === void 0 ? void 0 : parsed.status) === HTTP_STATUS_CONFLICT && parsed.body.error === SUPPORT_ERROR.REJOIN_UNAVAILABLE) {
                rejoinRequestIdRef.current = null;
                return 'unavailable';
            }
            if ((parsed === null || parsed === void 0 ? void 0 : parsed.status) === HTTP_STATUS_CONFLICT && parsed.body.support_session) {
                rejoinRequestIdRef.current = null;
                adopt(parsed.body.support_session);
                return 'moved';
            }
            setNotice(SUPPORT_COPY.REJOIN_FAILED);
            return 'failed';
        }
    }), [adopt]);
    const continueLateReply = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const convId = conversationIdRef.current;
        const { current } = sessionRef;
        if (!convId || !current)
            return;
        setLateWorking(true);
        setNotice(null);
        try {
            // The request was closed on the support side: it cannot be reopened. Say so; the customer
            // starts a new one from IPA.
            if ((yield rejoinCurrent(convId, current)) === 'unavailable')
                setLateClosed(true);
        }
        finally {
            setLateWorking(false);
        }
    }), [rejoinCurrent]);
    const continuePastChat = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        var _a;
        const convId = conversationIdRef.current;
        const { current } = sessionRef;
        if (!convId || !current)
            return false;
        setIsContinuing(true);
        setNotice(null);
        try {
            const outcome = yield rejoinCurrent(convId, current);
            if (outcome === 'unavailable')
                setNotice(SUPPORT_COPY.PAST_CHAT_CANNOT_CONTINUE);
            const isLive = isActiveSupportMode((_a = sessionRef.current) === null || _a === void 0 ? void 0 : _a.mode);
            if (outcome === 'moved' && !isLive) {
                // The server refused with the current state (live chat closed since the page was read, say):
                // a click that does nothing is the one outcome the customer must not be left with.
                setNotice(SUPPORT_COPY.REJOIN_FAILED);
                void refresh();
            }
            return isLive;
        }
        finally {
            setIsContinuing(false);
        }
    }), [rejoinCurrent, refresh]);
    const dismissLateReplyPrompt = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const convId = conversationIdRef.current;
        const { current } = sessionRef;
        if (!convId || !current)
            return;
        setLateDismissed(true); // gone at once; the composer stays with IPA
        try {
            yield dismissLateReply(convId, { support_session_id: current.id });
            const latest = sessionRef.current;
            if (latest && latest.id === current.id) {
                sessionRef.current = Object.assign(Object.assign({}, latest), { late_reply_pending: false });
                setHeld({ session: sessionRef.current, conversationId: conversationIdRef.current });
            }
        }
        catch (_a) {
            setLateDismissed(false);
            setNotice(SUPPORT_COPY.DISMISS_LATE_FAILED);
        }
    }), []);
    // ---- agent attachments ----------------------------------------------------------------
    const getAttachmentUrl = useCallback((fileId_1, ...args_1) => __awaiter(this, [fileId_1, ...args_1], void 0, function* (fileId, options = {}) {
        var _a, _b;
        const convId = conversationIdRef.current;
        if (!convId)
            throw new Error('No active conversation');
        const disposition = (_a = options.disposition) !== null && _a !== void 0 ? _a : SUPPORT_ATTACHMENT_DISPOSITION.INLINE;
        // The inline (preview) and attachment (download) URLs of one file are different URLs.
        const source = (_b = options.source) !== null && _b !== void 0 ? _b : 'support';
        const cacheKey = `${disposition}:${source}:${fileId}`;
        if (!options.forceRefresh) {
            // Reuse only while the short-lived URL has comfortable time left; the cache lives in
            // memory for this view and is never persisted.
            const cached = attachmentUrlsRef.current.get(cacheKey);
            if (cached && cached.expiresAt - Date.now() > SUPPORT_ATTACHMENT_URL_SKEW_MS)
                return { url: cached.url };
            const inFlight = attachmentRequestsRef.current.get(cacheKey);
            if (inFlight)
                return inFlight;
        }
        const request = (() => __awaiter(this, void 0, void 0, function* () {
            var _a;
            const fetched = yield fetchAttachmentUrl(convId, fileId, disposition, source);
            const expiresAt = Date.parse((_a = fetched.expires_at) !== null && _a !== void 0 ? _a : '');
            attachmentUrlsRef.current.set(cacheKey, {
                url: fetched.url,
                // An unreadable expiry is treated as already expired: always refetched, never reused.
                expiresAt: Number.isFinite(expiresAt) ? expiresAt : 0
            });
            return { url: fetched.url };
        }))().finally(() => {
            attachmentRequestsRef.current.delete(cacheKey);
        });
        attachmentRequestsRef.current.set(cacheKey, request);
        return request;
    }), []);
    const noticeTooManyFiles = useCallback((options) => {
        setNotice((options === null || options === void 0 ? void 0 : options.draftReturned)
            ? SUPPORT_COPY.TOO_MANY_FILES_DRAFT_KEPT(maxFilesRef.current)
            : SUPPORT_COPY.TOO_MANY_FILES(maxFilesRef.current));
    }, []);
    const dismissNotice = useCallback(() => setNotice(null), []);
    const dismissEnded = useCallback(() => { var _a; return setDismissedEndedId((_a = session === null || session === void 0 ? void 0 : session.id) !== null && _a !== void 0 ? _a : null); }, [session === null || session === void 0 ? void 0 : session.id]);
    const mode = (_b = session === null || session === void 0 ? void 0 : session.mode) !== null && _b !== void 0 ? _b : null;
    const showEndedBanner = session !== null &&
        mode === SUPPORT_MODE.ENDED &&
        dismissedEndedId !== session.id &&
        (endedSessionId === session.id || unreadCount > 0);
    const showLatePrompt = session !== null && mode === SUPPORT_MODE.ENDED && session.late_reply_pending === true && !lateDismissed;
    const lateReply = showLatePrompt ? { closed: lateClosed, isWorking: lateWorking } : null;
    return useMemo(() => {
        var _a;
        return ({
            state: (_a = snapshot.data) !== null && _a !== void 0 ? _a : null,
            session,
            mode,
            isActive: isActiveSupportMode(mode),
            canSendMessages: mode !== null && MESSAGEABLE_SUPPORT_MODES.includes(mode),
            unreadCount,
            maxFiles,
            isStarting,
            isEnding,
            showEndedBanner,
            notice,
            connectionState,
            dismissNotice,
            dismissEnded,
            lateReply,
            continueLateReply,
            dismissLateReplyPrompt,
            continuePastChat,
            isContinuing,
            getAttachmentUrl,
            startHandoff,
            sendMessage,
            retrySend,
            endChat,
            refresh,
            noteAiSendRefused,
            noticeTooManyFiles
        });
    }, [
        snapshot.data,
        session,
        mode,
        unreadCount,
        maxFiles,
        isStarting,
        isEnding,
        showEndedBanner,
        notice,
        connectionState,
        dismissNotice,
        dismissEnded,
        lateReply,
        continueLateReply,
        dismissLateReplyPrompt,
        continuePastChat,
        isContinuing,
        getAttachmentUrl,
        startHandoff,
        sendMessage,
        retrySend,
        endChat,
        refresh,
        noteAiSendRefused,
        noticeTooManyFiles
    ]);
}
